// Request util for api call
import request from '~/utils/request';

// Actions Types
import {
  SET_PHONE_TRACKING_DATA,
  SET_PHONE_TRACKING_LOADER,
  SET_PHONE_TRACKING_DETAIL_DATA,
  SET_PHONE_TRACKING_DETAIL_LOADER,
  SET_PHONE_TRACKING_HISTORY_DATA,
  SET_PHONE_TRACKING_HISTORY_LOADER,
  SET_PHONE_TRACKING_SMS_HISTORY_DATA,
  SET_PHONE_TRACKING_SMS_HISTORY_LOADER,
  SET_PHONE_TRACKING_LOOKUP_DATA,
  SET_PHONE_TRACKING_LOOKUP_LOADER,
  CREATE_PHONE_TRACKING_NUMBER,
  UPDATE_PHONE_TRACKING_NUMBER,
  RELEASE_PHONE_TRACKING_NUMBER,
} from './action-types';

// Utils
import { DEFAULT_PAGINATION } from '~/utils/constants';

/**
 * Fetch all phone tracking numbers
 * GET /tracking-phone-numbers
 */
const fetchPhoneTrackingNumbers = (payload) => async (dispatch) => {
  try {
    dispatch({ type: SET_PHONE_TRACKING_LOADER, payload: true });

    let url = '/tracking-phone-numbers';
    
    let queryPayload = {
      page: payload?.pagination?.page ? payload.pagination.page : DEFAULT_PAGINATION.page,
      pageSize: payload?.pagination?.pageSize
        ? payload.pagination.pageSize
        : DEFAULT_PAGINATION.pageSize,
    };

    if (payload?.search?.length) {
      queryPayload.search = payload.search;
    }

    if (payload?.customTemplateId) {
      queryPayload.customTemplateId = payload.customTemplateId;
    }

    const response = await request.get(url, queryPayload);

    if (response.status === 200) {
      const phoneTrackingData = {
        rows: response.data.data.rows || response.data.data,
        count: response.data.data.count || response.data.data.length,
        to: response.data.data.to,
        from: response.data.data.from,
        currentPage: response.data.data.currentPage,
        lastPage: response.data.data.lastPage,
        perPage: response.data.data.perPage,
        totalRecordsInDB: response.data.data.totalRecordsInDB
      };
      dispatch({ type: SET_PHONE_TRACKING_DATA, payload: phoneTrackingData });
    }

    return response;
  } catch (error) {
    return error;
  } finally {
    dispatch({ type: SET_PHONE_TRACKING_LOADER, payload: false });
  }
};

/**
 * Fetch single phone tracking number
 * GET /tracking-phone-numbers/:id
 */
const fetchPhoneTrackingNumber = (id) => async (dispatch) => {
  try {
    dispatch({ type: SET_PHONE_TRACKING_DETAIL_LOADER, payload: true });

    const response = await request.get(`/tracking-phone-numbers/${id}`);

    if (response.status === 200) {
      dispatch({ type: SET_PHONE_TRACKING_DETAIL_DATA, payload: response.data.data });
    }

    return response;
  } catch (error) {
    return error;
  } finally {
    dispatch({ type: SET_PHONE_TRACKING_DETAIL_LOADER, payload: false });
  }
};

/**
 * Lookup phone numbers by area code
 * POST /tracking-phone-numbers/lookup
 */
const lookupPhoneNumbers = (payload) => async (dispatch) => {
  try {
    dispatch({ type: SET_PHONE_TRACKING_LOOKUP_LOADER, payload: true });

    const response = await request.post('/tracking-phone-numbers/lookup', {
      areaCode: payload.areaCode,
    });
  
    if (response.status === 200) {
      dispatch({ type: SET_PHONE_TRACKING_LOOKUP_DATA, payload: response.data.data });
    } else {
      dispatch({ type: SET_PHONE_TRACKING_LOOKUP_LOADER, payload: false });
    }

    return response;
  } catch (error) {
    return error.response;
  } finally {
    dispatch({ type: SET_PHONE_TRACKING_LOOKUP_LOADER, payload: false });
  }
};

/**
 * Create new phone tracking number
 * POST /tracking-phone-numbers
 */
const createPhoneTrackingNumber = (payload) => async (dispatch) => {
  try {
    const body = {
      name: payload.name,
      phoneNumber: payload.phoneNumber,
      forwardingPhoneNumber: payload.forwardingPhoneNumber,
    };
    if (payload.smsForwardingEmail) body.smsForwardingEmail = payload.smsForwardingEmail;
    if (payload.customTemplateId) body.customTemplateId = payload.customTemplateId;

    const response = await request.post('/tracking-phone-numbers', body);

    if (response.status === 200 || response.status === 201) {
      dispatch({ type: CREATE_PHONE_TRACKING_NUMBER, payload: response.data.data });
    }

    return response;
  } catch (error) {
    return error.response;
  }
};

/**
 * Update phone tracking number
 * PUT /tracking-phone-numbers/:id
 */
const updatePhoneTrackingNumber = (payload) => async (dispatch) => {
  try {
    const body = {
      name: payload.updatedName,
      forwardingPhoneNumber: payload.forwardingPhoneNumber,
      smsForwardingEmail: payload.smsForwardingEmail || null,
    };

    const response = await request.put(`/tracking-phone-numbers/${payload.id}`, body);

    if (response.status === 200) {
      dispatch({ type: UPDATE_PHONE_TRACKING_NUMBER, payload: response.data.data });
    }

    return response;
  } catch (error) {
    return error.response;
  }
};

/**
 * Get phone tracking history
 * GET /tracking-phone-numbers/history/:id
 */
const fetchPhoneTrackingHistory = (payload) => async (dispatch) => {
  try {
    dispatch({
      type: SET_PHONE_TRACKING_HISTORY_DATA,
      payload: { rows: [], count: 0, to: null, from: null, currentPage: null, lastPage: null, perPage: DEFAULT_PAGINATION.pageSize },
    });
    dispatch({ type: SET_PHONE_TRACKING_HISTORY_LOADER, payload: true });

    const response = await request.get(`/tracking-phone-numbers/history/${payload.id}`, {
      page: payload?.pagination?.page ? payload.pagination.page : DEFAULT_PAGINATION.page,
      pageSize: payload?.pagination?.pageSize
        ? payload.pagination.pageSize
        : DEFAULT_PAGINATION.pageSize,
    });

    if (response.status === 200) {
      dispatch({ type: SET_PHONE_TRACKING_HISTORY_DATA, payload: response.data.data });
    }

    return response;
  } catch (error) {
    return error;
  } finally {
    dispatch({ type: SET_PHONE_TRACKING_HISTORY_LOADER, payload: false });
  }
};

/**
 * Fetch SMS tracking history for a phone number
 * GET /tracking-phone-numbers/sms-history/:id
 */
const fetchSmsTrackingHistory = (payload) => async (dispatch) => {
  try {
    dispatch({
      type: SET_PHONE_TRACKING_SMS_HISTORY_DATA,
      payload: { rows: [], count: 0, to: null, from: null, currentPage: null, lastPage: null, perPage: DEFAULT_PAGINATION.pageSize },
    });
    dispatch({ type: SET_PHONE_TRACKING_SMS_HISTORY_LOADER, payload: true });

    const response = await request.get(`/tracking-phone-numbers/sms-history/${payload.id}`, {
      page: payload?.pagination?.page ? payload.pagination.page : DEFAULT_PAGINATION.page,
      pageSize: payload?.pagination?.pageSize
        ? payload.pagination.pageSize
        : DEFAULT_PAGINATION.pageSize,
    });

    if (response.status === 200) {
      const responseData = response.data?.data;
      const rows = Array.isArray(responseData) ? responseData : responseData?.rows || [];
      const smsHistoryData = {
        rows,
        count: responseData?.count ?? rows.length,
        to: responseData?.to ?? (rows.length || null),
        from: responseData?.from ?? (rows.length ? 1 : null),
        currentPage: responseData?.currentPage ?? DEFAULT_PAGINATION.page,
        lastPage: responseData?.lastPage ?? (rows.length ? 1 : 0),
        perPage: responseData?.perPage ?? DEFAULT_PAGINATION.pageSize,
      };

      dispatch({ type: SET_PHONE_TRACKING_SMS_HISTORY_DATA, payload: smsHistoryData });
    }

    return response;
  } catch (error) {
    return error;
  } finally {
    dispatch({ type: SET_PHONE_TRACKING_SMS_HISTORY_LOADER, payload: false });
  }
};

/**
 * Release phone tracking number
 * POST /tracking-phone-numbers/release/:id
 */
const releasePhoneTrackingNumber = (id) => async (dispatch) => {
  try {
    const response = await request.post(`/tracking-phone-numbers/release/${id}`);

    if (response.status === 200) {
      dispatch({ type: RELEASE_PHONE_TRACKING_NUMBER, payload: { id } });
    }

    return response;
  } catch (error) {
    return error.response;
  }
};

export {
  fetchPhoneTrackingNumbers,
  fetchPhoneTrackingNumber,
  lookupPhoneNumbers,
  createPhoneTrackingNumber,
  updatePhoneTrackingNumber,
  fetchPhoneTrackingHistory,
  fetchSmsTrackingHistory,
  releasePhoneTrackingNumber,
};
